import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Input,
  Label,
  FormGroup,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import UserInfoModal from "../../components/Common/UserInfoModal";
import UserBroadcastsModal from "./helper/userBroadcastsModal";
import { getReportedBroadcastList } from "./helper/reportedBroadcastApi";

const ReportedBroadcastPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [broadcastsModal, setBroadcastsModal] = useState(false);
  const [selectedUserForBroadcasts, setSelectedUserForBroadcasts] = useState(null);

  // Filter states
  const [keyword, setKeyword] = useState("");
  const [isSuspended, setIsSuspended] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Refs for optimization
  const keywordTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  // Debounced keyword
  const [debouncedKeyword, setDebouncedKeyword] = useState(null);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">User ID</span>,
        selector: (row) => row.user_id,
        sortable: true,
        cell: (row) => (
          <div>
            <div
              className="text-primary cursor-pointer"
              onClick={() => handleUserIdClick(row.user_id)}
              style={{ cursor: "pointer" }}
            >
              {row.user_id}
            </div>
          </div>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Username</span>,
        selector: (row) => row.username,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Report Count</span>,
        selector: (row) => row.report_count,
        sortable: true,
        cell: (row) => (
          <Badge color="danger" className="px-3 py-1">
            {row.report_count || 0}
          </Badge>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">First Reported At</span>,
        selector: (row) => row.first_reported_at,
        sortable: true,
        cell: (row) => {
          if (!row.first_reported_at) return "-";
          const date = new Date(row.first_reported_at);
          return date.toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Last Reported At</span>,
        selector: (row) => row.last_reported_at,
        sortable: true,
        cell: (row) => {
          if (!row.last_reported_at) return "-";
          const date = new Date(row.last_reported_at);
          return date.toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Actions</span>,
        cell: (row) => (
          <Button
            color="info"
            size="sm"
            onClick={() => handleViewBroadcasts(row)}
            className="px-3"
          >
            <i className="mdi mdi-broadcast me-1"></i>Show Broadcasts
          </Button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    []
  );

  const handleUserIdClick = useCallback((userId) => {
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleViewBroadcasts = useCallback((userReport) => {
    setSelectedUserForBroadcasts({
      userId: userReport.user_id,
      username: userReport.username,
    });
    setBroadcastsModal(true);
  }, []);

  const fetchList = useCallback(async (debouncedKeyword = null) => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        limit,
        page: currentPage,
        keyword: debouncedKeyword !== null ? debouncedKeyword : (keyword || undefined),
        is_suspended: isSuspended !== "" ? isSuspended : undefined,
      };

      const result = await getReportedBroadcastList(filters);

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(result.data);
      setTotalRows(result.pagination.totalRecords);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching reported broadcast list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [keyword, isSuspended, currentPage, limit]);

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (keywordTimeoutRef.current) {
        clearTimeout(keywordTimeoutRef.current);
      }
    };
  }, []);

  // Debounce keyword search
  useEffect(() => {
    if (keywordTimeoutRef.current) {
      clearTimeout(keywordTimeoutRef.current);
    }

    keywordTimeoutRef.current = setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, 1000);

    return () => {
      if (keywordTimeoutRef.current) {
        clearTimeout(keywordTimeoutRef.current);
      }
    };
  }, [keyword]);

  // Fetch when debounced keyword changes
  useEffect(() => {
    if (hasInitialFetchedRef.current && debouncedKeyword !== null) {
      setCurrentPage(1);
      fetchList(debouncedKeyword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword]);

  // Fetch when page changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Fetch when isSuspended filter changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      setCurrentPage(1);
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuspended]);

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    switch (filterType) {
      case "keyword":
        setKeyword(value);
        break;
      case "isSuspended":
        setIsSuspended(value);
        break;
      default:
        break;
    }
  };

  const clearFilters = useCallback(() => {
    setKeyword("");
    setIsSuspended("");
    setCurrentPage(1);
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Reported Broadcast" breadcrumbItem="Reported Broadcast" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-broadcast-off me-2"></i>Reported Broadcast
                  </h4>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label>Search Username</Label>
                        <Input
                          type="text"
                          placeholder="Search by username..."
                          value={keyword}
                          onChange={(e) => handleFilterChange("keyword", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Is Suspended</Label>
                        <Input
                          type="select"
                          value={isSuspended}
                          onChange={(e) => handleFilterChange("isSuspended", e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="true">Suspended</option>
                          <option value="false">Not Suspended</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <Button color="secondary" onClick={clearFilters} className="me-2">
                        <i className="mdi mdi-filter-remove me-1"></i>Clear Filters
                      </Button>
                    </Col>
                  </Row>

                  {/* Data Table */}
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={(page) => setCurrentPage(page)}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="mdi mdi-broadcast-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                        <p className="mt-3 text-muted">No reported broadcasts found</p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Refresh the list after user action
          fetchList();
        }}
      />

      <UserBroadcastsModal
        isOpen={broadcastsModal}
        toggle={() => {
          setBroadcastsModal(false);
          setSelectedUserForBroadcasts(null);
        }}
        userId={selectedUserForBroadcasts?.userId}
        username={selectedUserForBroadcasts?.username}
      />
    </React.Fragment>
  );
};

export default ReportedBroadcastPage;

